import { useEffect, useState } from "react";
import { usePage } from "@inertiajs/react";
import { CheckCircle2, AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/cn";

type FlashProps = {
  flash?: { notice?: string | null; alert?: string | null };
};

type Toast = { kind: "notice" | "alert"; message: string };

/**
 * Surfaces Rails flash messages (shared into Inertia props via
 * `inertia_share` in ApplicationController) as a transient toast. Re-fires on
 * every navigation that carries a flash — Inertia hands a fresh props object
 * each response, so even repeated identical messages re-trigger.
 */
export function FlashToast() {
  const page = usePage<FlashProps>();
  const flash = page.props.flash;
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => {
    const next: Toast | null = flash?.notice
      ? { kind: "notice", message: flash.notice }
      : flash?.alert
        ? { kind: "alert", message: flash.alert }
        : null;
    if (!next) return;
    setToast(next);
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [flash]);

  if (!toast) return null;

  const isAlert = toast.kind === "alert";

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex justify-end" role="status" aria-live="polite">
      <div
        className={cn(
          "flex items-center gap-2 rounded-[var(--radius-md)] border px-3 py-2 text-sm shadow-[var(--shadow-3)]",
          "animate-in fade-in-0 slide-in-from-bottom-2",
          isAlert
            ? "border-danger-fg/30 bg-danger-bg text-danger-fg"
            : "border-success-fg/30 bg-success-bg text-success-fg",
        )}
      >
        {isAlert ? <AlertTriangle className="size-4 shrink-0" /> : <CheckCircle2 className="size-4 shrink-0" />}
        <span>{toast.message}</span>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => setToast(null)}
          className="ml-1 opacity-70 hover:opacity-100"
        >
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  );
}
